import { Building2, Home, UsersRound } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle } from '@/shared/components';
import { ROLES, type Role } from '@/shared/auth';
import type { CompanyResponse, UserAccessResponse } from '@/shared/api';
import { roleLabel, userRole } from '../userStatus';
import { UserAccessPropertyScopeList } from './UserAccessPropertyScopeList';

interface Props {
  access: UserAccessResponse | null;
  company: CompanyResponse | null;
  loading: boolean;
  error: boolean;
  onEditAccess: () => void;
  onRetry: () => void;
}

const ROLE_SCOPE_HINT: Record<Role, string> = {
  PlatformAdmin: 'Verwaltet die Plattform selbst; der Zugriff hängt nicht an Zuweisungen.',
  WorkspaceAdmin: 'Sieht und verwaltet den gesamten Workspace; Zuweisungen ändern daran nichts.',
  Staff: 'Sieht und bearbeitet den gesamten operativen Bestand.',
  Housekeeping: 'Sieht ausschließlich die zugewiesenen Apartments und deren Reinigungsaufträge.',
  HousekeepingManager: 'Sieht und steuert Reinigungsaufgaben der zugewiesenen Apartments.',
  Owner: 'Sieht ausschließlich die zugewiesenen Apartments, ohne Gastdaten.',
  Guest: 'Erreicht nur das Gastportal seiner eigenen Buchung.',
};

/** Gespeicherte Quellen und effektive Sicht bleiben klar getrennt und serverautoritativ. */
export function UserAccessCard({ access, company, loading, error, onEditAccess, onRetry }: Props) {
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rolle und Zugriff</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-ink-faint text-sm">Zugriff konnte nicht geladen werden.</p>
          <Button
            variant="ghost"
            size="sm"
            className="border-line min-h-11 border"
            onClick={onRetry}
          >
            Erneut versuchen
          </Button>
        </CardBody>
      </Card>
    );
  }

  if (loading || !access) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rolle und Zugriff</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-ink-faint text-sm">Zugriff wird geladen…</p>
        </CardBody>
      </Card>
    );
  }

  const role = userRole(access.user);
  const scopeHint = ROLE_SCOPE_HINT[role as Role];
  const canEdit = role !== ROLES.Guest && role !== ROLES.PlatformAdmin;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Rolle und Zugriff</CardTitle>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              className="border-line min-h-11 border"
              onClick={onEditAccess}
            >
              Zugriff bearbeiten
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody className="space-y-6">
        <section>
          <p className="text-ink-soft text-xs font-medium uppercase tracking-wider">
            Gespeicherte Quellen
          </p>
          <p className="text-ink mt-2 font-serif text-lg">{roleLabel(role)}</p>
          {scopeHint && <p className="text-ink-soft mt-1 text-sm">{scopeHint}</p>}

          <div className="border-line/50 mt-4 grid gap-3 border-t pt-4 sm:grid-cols-2">
            <div className="flex min-w-0 items-start gap-2.5">
              <Building2 size={17} className="text-sage mt-0.5 shrink-0" aria-hidden />
              <div className="min-w-0">
                <p className="text-ink-soft text-xs">Firma</p>
                <p className="text-ink truncate text-sm">
                  {access.user.companyId
                    ? (company?.name ?? 'Firma wird geladen…')
                    : 'Keine Firmenzuordnung'}
                </p>
              </div>
            </div>
            <div className="flex min-w-0 items-start gap-2.5">
              <Home size={17} className="text-postmark mt-0.5 shrink-0" aria-hidden />
              <div className="min-w-0">
                <p className="text-ink-soft text-xs">Direkt freigegeben</p>
                <p className="text-ink text-sm">
                  {access.directProperties.length === 0
                    ? 'Keine direkten Apartments'
                    : `${access.directProperties.length} ${
                        access.directProperties.length === 1 ? 'Apartment' : 'Apartments'
                      }`}
                </p>
              </div>
            </div>
          </div>

          {access.directProperties.length > 0 && (
            <div className="mt-4">
              <UserAccessPropertyScopeList properties={access.directProperties} />
            </div>
          )}
        </section>

        <section className="border-line/50 border-t pt-5">
          <div className="flex items-start gap-2.5">
            <UsersRound size={17} className="text-postmark mt-0.5 shrink-0" aria-hidden />
            <div>
              <p className="text-ink-soft text-xs font-medium uppercase tracking-wider">
                Effektiver Zugriff
              </p>
              <p className="text-ink-soft mt-1 text-sm">
                Vom Server aus Rolle, Firma, Direktfreigaben und vorhandenen Teams aufgelöst.
              </p>
            </div>
          </div>
          <div className="mt-4">
            {access.isUnrestricted ? (
              <p className="border-sage/30 bg-sage/10 text-ink rounded-md border px-3 py-3 text-sm">
                Diese Rolle sieht den gesamten Workspace. Einzelne gespeicherte Quellen begrenzen
                diese Sicht nicht.
              </p>
            ) : access.effectiveProperties.length === 0 ? (
              <p className="text-ink-soft text-sm">
                Keine wirksame Freigabe — dieser Benutzer sieht derzeit keine Apartments.
              </p>
            ) : (
              <UserAccessPropertyScopeList properties={access.effectiveProperties} />
            )}
          </div>
        </section>
      </CardBody>
    </Card>
  );
}
